<template>
  <v-dialog :value="value" @input="$emit('input', $event)" max-width="1300" scrollable>
    <v-card>
      <v-card-title class="text-subtitle-1 grey--text">
        <v-icon small class="mr-2" color="deep-orange">mdi-compare-horizontal</v-icon>
        Confronto Piano / Arrivi
        <span v-if="stagione_attiva" class="font-weight-light ml-2 text-body-2">
          — {{ nomeStagione }}
        </span>
        <v-spacer />
        <v-btn icon small @click="$emit('input', false)">
          <v-icon small>mdi-close</v-icon>
        </v-btn>
      </v-card-title>
      <v-divider />

      <v-card-text class="pa-0" style="height: 75vh;">

        <!-- Stato vuoto -->
        <div v-if="!prodotti.length" class="pa-8 text-center grey--text">
          <v-icon large color="grey lighten-2">mdi-sprout-outline</v-icon>
          <div class="mt-2 text-caption">Nessun prodotto pianificato per la stagione corrente</div>
        </div>

        <template v-else>
          <!-- KPI globali -->
          <div class="pa-4 pb-0">
            <v-row dense>
              <v-col cols="6" sm="3" v-for="kpi in kpi_globali" :key="kpi.label">
                <v-sheet outlined rounded class="pa-3 text-center">
                  <div class="text-overline grey--text">{{ kpi.label }}</div>
                  <div class="text-h5 font-weight-bold" :class="kpi.color + '--text'">{{ kpi.valore }}</div>
                  <div class="text-caption grey--text">{{ kpi.sub }}</div>
                </v-sheet>
              </v-col>
            </v-row>
          </div>

          <!-- Toolbar filtri -->
          <div class="d-flex align-center px-4 py-3" style="gap: 8px; flex-wrap: wrap;">
            <v-btn-toggle v-model="filtro_stato" mandatory dense borderless color="deep-orange">
              <v-btn small value="tutti" text>Tutte</v-btn>
              <v-btn small value="scoperta" text>
                <v-icon small color="red" class="mr-1">mdi-alert-circle</v-icon>Non ordinate
              </v-btn>
              <v-btn small value="parziale" text>
                <v-icon small color="orange" class="mr-1">mdi-alert</v-icon>Parziali
              </v-btn>
              <v-btn small value="coperta" text>
                <v-icon small color="green" class="mr-1">mdi-check-circle</v-icon>Coperte
              </v-btn>
              <v-btn small value="eccedenza" text>
                <v-icon small color="blue" class="mr-1">mdi-information</v-icon>Eccedenze
              </v-btn>
            </v-btn-toggle>
            <v-btn-toggle v-model="filtro_prop" mandatory dense borderless>
              <v-btn small value="tutti" text>Tutte</v-btn>
              <v-btn small value="seme" text>
                <v-icon small color="brown lighten-2" class="mr-1">mdi-seed</v-icon>Seme
              </v-btn>
              <v-btn small value="talea" text>
                <v-icon small color="green" class="mr-1">mdi-leaf</v-icon>Talea
              </v-btn>
            </v-btn-toggle>
            <v-text-field
              v-model="filtro_nome"
              dense
              outlined
              hide-details
              clearable
              placeholder="Filtra per nome…"
              prepend-inner-icon="mdi-magnify"
              :error="filtro_nome_regex_error"
              style="max-width: 240px;"
            />
            <v-spacer />
            <span class="text-caption grey--text">{{ righe_filtrate.length }} specie</span>
          </div>
          <v-divider />

          <!-- Caricamento arrivi -->
          <v-progress-linear v-if="loading_arrivi" indeterminate color="deep-orange" />
          <v-alert v-else-if="errore_arrivi" type="error" dense text class="mx-4 mt-3">{{ errore_arrivi }}</v-alert>

          <!-- Tabella confronto -->
          <div class="px-4 py-4" style="overflow-y: auto; height: calc(75vh - 220px);">
            <v-card outlined>
            <v-simple-table>
              <thead>
                <tr style="position: sticky; top: 0; z-index: 2; background: #fafafa;">
                  <th style="min-width: 200px;">Specie</th>
                  <th class="text-right">Necessario</th>
                  <th class="text-right">Ordinato</th>
                  <th class="text-right">Delta</th>
                  <th style="min-width: 180px;">Copertura</th>
                  <th>Stato</th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="riga in righe_filtrate"
                  :key="riga.id"
                  :style="rigaStyle(riga)"
                >
                  <!-- Specie -->
                  <td>
                    <div class="d-flex align-center" style="gap: 6px;">
                      <v-avatar size="28">
                        <v-img :src="avatarSpecie(riga.id)" />
                      </v-avatar>
                      <div>
                        <div class="text-body-2">{{ riga.nome }}</div>
                        <div class="d-flex align-center" style="gap: 3px;">
                          <v-icon x-small :color="riga.propagazione === 1 ? 'brown lighten-2' : 'green'">
                            {{ riga.propagazione === 1 ? 'mdi-seed' : 'mdi-leaf' }}
                          </v-icon>
                          <span class="text-caption grey--text">{{ riga.propagazione === 1 ? 'seme' : 'talea' }}</span>
                        </div>
                      </div>
                    </div>
                  </td>

                  <!-- Necessario -->
                  <td class="text-right">
                    <span class="font-weight-medium">{{ formatNum(riga.necessario) }}</span>
                    <span class="text-caption grey--text ml-1">pz</span>
                  </td>

                  <!-- Ordinato -->
                  <td class="text-right">
                    <span class="font-weight-medium" :class="riga.ordinato === 0 ? 'red--text' : ''">
                      {{ formatNum(riga.ordinato) }}
                    </span>
                    <span class="text-caption grey--text ml-1">pz</span>
                  </td>

                  <!-- Delta -->
                  <td class="text-right">
                    <span
                      class="font-weight-bold"
                      :class="riga.delta >= 0 ? 'green--text' : 'red--text'"
                    >
                      {{ riga.delta >= 0 ? '+' : '' }}{{ formatNum(riga.delta) }}
                    </span>
                  </td>

                  <!-- Barra copertura -->
                  <td>
                    <div class="d-flex align-center" style="gap: 6px;">
                      <v-progress-linear
                        :value="Math.min(riga.perc, 100)"
                        :color="barColor(riga)"
                        height="8"
                        rounded
                        style="min-width: 100px; flex: 1;"
                      />
                      <span class="text-caption font-weight-medium" style="min-width: 38px; text-align: right;">
                        {{ riga.perc }}%
                      </span>
                    </div>
                  </td>

                  <!-- Stato chip -->
                  <td>
                    <v-chip x-small :color="statoColor(riga)" dark>
                      {{ statoLabel(riga) }}
                    </v-chip>
                  </td>
                </tr>

                <!-- Riga non pianificate (ordinate ma non nel piano) -->
                <template v-if="filtro_stato === 'tutti' && extra_arrivi.length">
                  <tr>
                    <td colspan="6" class="text-caption text-uppercase grey--text font-weight-bold py-2 px-4"
                        style="background: #f5f5f5">
                      Ordinate ma non pianificate
                    </td>
                  </tr>
                  <tr v-for="riga in extra_arrivi" :key="'extra-' + riga.id" style="background: #e3f2fd;">
                    <td>
                      <div class="d-flex align-center" style="gap: 6px;">
                        <v-avatar size="28"><v-img :src="avatarSpecie(riga.id)" /></v-avatar>
                        <div>
                          <div class="text-body-2">{{ riga.nome }}</div>
                          <div class="d-flex align-center" style="gap: 3px;">
                            <v-icon x-small :color="riga.propagazione === 1 ? 'brown lighten-2' : 'green'">
                              {{ riga.propagazione === 1 ? 'mdi-seed' : 'mdi-leaf' }}
                            </v-icon>
                            <span class="text-caption grey--text">{{ riga.propagazione === 1 ? 'seme' : 'talea' }}</span>
                          </div>
                        </div>
                      </div>
                    </td>
                    <td class="text-right text-caption grey--text">—</td>
                    <td class="text-right">
                      <span class="font-weight-medium blue--text">{{ formatNum(riga.ordinato) }}</span>
                      <span class="text-caption grey--text ml-1">pz</span>
                    </td>
                    <td colspan="2"></td>
                    <td>
                      <v-chip x-small color="blue lighten-3" dark>non pianificata</v-chip>
                    </td>
                  </tr>
                </template>

                <tr v-if="!righe_filtrate.length && !(filtro_stato === 'tutti' && extra_arrivi.length)">
                  <td colspan="6" class="text-center grey--text text-caption py-6">Nessuna specie corrisponde al filtro</td>
                </tr>
              </tbody>
            </v-simple-table>
            </v-card>
          </div>
        </template>
      </v-card-text>
    </v-card>
  </v-dialog>
</template>

<script>
export default {
  name: "ConfruntoPianoArriviDialog",
  props: {
    value:           { type: Boolean, default: false },
    prodotti:        { type: Array,   default: () => [] },
    stagione_attiva: { type: Object,  default: null },
  },
  data() {
    return {
      filtro_stato:   "tutti",
      filtro_prop:    "tutti",
      filtro_nome:    "",
      loading_arrivi: false,
      errore_arrivi:  "",
    };
  },
  watch: {
    value(val) {
      if (val) this.caricaArrivi();
    },
  },
  computed: {
    nomeStagione() {
      if (!this.stagione_attiva) return "";
      const tipo = this.stagione_attiva.tipo_stagione?.nome || this.stagione_attiva.nome || "";
      return `${tipo} ${this.stagione_attiva.anno || ""}`.trim();
    },
    anno() {
      return this.stagione_attiva?.anno || new Date().getFullYear();
    },
    specie_per_id() {
      const map = {};
      for (const s of (this.$store.getters.get_specie || [])) map[String(s.id)] = s;
      return map;
    },
    fornitori_per_id() {
      const map = {};
      for (const f of (this.$store.getters.get_fornitori || [])) map[f.id] = f;
      return map;
    },
    calendario() {
      return this.$store.getters.get_calendario_arrivi(this.anno);
    },

    // ── Piante necessarie per specie dal Piano ──────────────────────────
    // Formula: quantita_produzione × quante volte la specie compare nei componenti
    necessario_per_specie() {
      const map = {};
      for (const prodotto of this.prodotti) {
        const qta = Number(prodotto.quantita_produzione) || 0;
        if (!qta) continue;
        const componenti = prodotto.componenti || [];
        const countPerSpecie = {};
        for (const c of componenti) {
          const sid = String(c.id_specie);
          if (!sid || sid === "null" || sid === "undefined") continue;
          countPerSpecie[sid] = (countPerSpecie[sid] || 0) + 1;
        }
        for (const [sid, countPerVaso] of Object.entries(countPerSpecie)) {
          map[sid] = (map[sid] || 0) + qta * countPerVaso;
        }
      }
      return map;
    },

    // ── Piante ordinate per specie dagli Arrivi ─────────────────────────
    // Filtra per stagione tramite arrivo.stagioni e p.id_stagione.
    // Retrocompat: se i campi mancano (dati storici), l'elemento viene incluso.
    ordinato_per_specie() {
      const map = {};
      if (!this.calendario?.settimane) return map;
      const id_stagione = this.stagione_attiva?.id ?? null;
      for (const sett of this.calendario.settimane) {
        for (const arrivo of (sett.arrivi || [])) {
          if (id_stagione != null && arrivo.stagioni != null && !arrivo.stagioni.includes(id_stagione)) continue;
          for (const p of (arrivo.prodotti || [])) {
            if (id_stagione != null && p.id_stagione != null && p.id_stagione !== id_stagione) continue;
            const sid = String(p.id_specie);
            map[sid] = (map[sid] || 0) + (Number(p.qta) || 0);
          }
        }
      }
      return map;
    },

    // ── Righe del confronto (solo specie pianificate) ───────────────────
    righe_confronto() {
      return Object.entries(this.necessario_per_specie).map(([sid, necessario]) => {
        const sp        = this.specie_per_id[sid];
        const ordinato  = this.ordinato_per_specie[sid] || 0;
        const delta     = ordinato - necessario;
        const perc      = necessario > 0 ? Math.round((ordinato / necessario) * 100) : 0;
        return {
          id:           sid,
          nome:         sp?.nome || "Specie #" + sid,
          propagazione: sp?.propagazione || 0,
          necessario,
          ordinato,
          delta,
          perc,
        };
      }).sort((a, b) => a.perc - b.perc); // prima le meno coperte
    },

    // ── Specie ordinate ma NON nel piano ───────────────────────────────
    extra_arrivi() {
      const pianificate = new Set(Object.keys(this.necessario_per_specie));
      const extra = [];
      for (const [sid, ordinato] of Object.entries(this.ordinato_per_specie)) {
        if (pianificate.has(sid)) continue;
        const sp = this.specie_per_id[sid];
        extra.push({
          id:           sid,
          nome:         sp?.nome || "Specie #" + sid,
          propagazione: sp?.propagazione || 0,
          ordinato,
        });
      }
      return extra.sort((a, b) => b.ordinato - a.ordinato);
    },

    // ── Filtro applicato ────────────────────────────────────────────────
    filtro_nome_regex_error() {
      if (!this.filtro_nome) return false;
      try { new RegExp(this.filtro_nome, 'i'); return false; } catch { return true; }
    },
    righe_filtrate() {
      let righe = this.righe_confronto;
      if (this.filtro_prop === "seme")  righe = righe.filter((r) => r.propagazione === 1);
      if (this.filtro_prop === "talea") righe = righe.filter((r) => r.propagazione === 2);
      if (this.filtro_nome && !this.filtro_nome_regex_error) {
        const re = new RegExp(this.filtro_nome, 'i');
        righe = righe.filter((r) => re.test(r.nome));
      }
      if (this.filtro_stato === "scoperta")  return righe.filter((r) => r.ordinato === 0);
      if (this.filtro_stato === "parziale")  return righe.filter((r) => r.ordinato > 0 && r.perc < 100);
      if (this.filtro_stato === "coperta")   return righe.filter((r) => r.perc >= 100 && r.delta <= 0);
      if (this.filtro_stato === "eccedenza") return righe.filter((r) => r.delta > 0);
      return righe;
    },

    // ── KPI globali ─────────────────────────────────────────────────────
    kpi_globali() {
      const righe = this.righe_confronto;
      const totNecessario = righe.reduce((s, r) => s + r.necessario, 0);
      const totOrdinato   = righe.reduce((s, r) => s + r.ordinato,   0);
      const copRatio      = totNecessario > 0 ? Math.round((totOrdinato / totNecessario) * 100) : 0;
      const scoperte      = righe.filter((r) => r.ordinato === 0).length;
      const parziali      = righe.filter((r) => r.ordinato > 0 && r.perc < 100).length;
      return [
        { label: "Copertura globale", valore: copRatio + "%",              sub: "pz ordinati / necessari",  color: copRatio >= 100 ? "green" : copRatio >= 50 ? "orange" : "red" },
        { label: "Non ordinate",      valore: scoperte,                    sub: "specie senza ordini",      color: scoperte  > 0 ? "red"    : "green" },
        { label: "Parziali",          valore: parziali,                    sub: "specie sotto il 100%",     color: parziali  > 0 ? "orange" : "green" },
        { label: "Totale necessario", valore: this.formatNum(totNecessario), sub: "piante",        color: "black" },
      ];
    },
  },
  methods: {
    formatNum(n) {
      return (Number(n) || 0).toLocaleString("it-IT");
    },
    avatarSpecie(id) {
      const sp = this.specie_per_id[id];
      return `${process.env.VUE_APP_API_ROOT}/specie/${id}/img?md5=${sp?.img_md5 || ""}`;
    },
    barColor(riga) {
      if (riga.ordinato === 0)    return "red";
      if (riga.perc < 50)         return "red lighten-1";
      if (riga.perc < 100)        return "orange";
      return "green";
    },
    statoColor(riga) {
      if (riga.ordinato === 0)    return "red";
      if (riga.perc < 100)        return "orange";
      if (riga.delta > 0)         return "blue";
      return "green";
    },
    statoLabel(riga) {
      if (riga.ordinato === 0)    return "non ordinata";
      if (riga.perc < 100)        return "parziale " + riga.perc + "%";
      if (riga.delta > 0)         return "eccedenza +" + this.formatNum(riga.delta);
      return "coperta";
    },
    rigaStyle(riga) {
      if (riga.ordinato === 0)  return "background: #fff8f8;";
      if (riga.perc < 100)      return "background: #fffde7;";
      if (riga.delta > 0)       return "background: #e3f2fd;";
      return "";
    },
    async caricaArrivi() {
      if (!this.anno) return;
      if (this.$store.getters.get_calendario_arrivi(this.anno)) return;
      this.loading_arrivi = true;
      this.errore_arrivi  = "";
      try {
        await this.$store.dispatch("fetch_calendario_arrivi", this.anno);
      } catch {
        this.errore_arrivi = "Impossibile caricare gli arrivi per l'anno " + this.anno + ".";
      } finally {
        this.loading_arrivi = false;
      }
    },
  },
};
</script>
